/**
 * Task Manager Module
 * Handles task loading, parsing, and pre-calculation of task geometry
 */

import {
  haversineDistance,
  calculateBearing,
  normalizeAngle,
  calculateTurnpointBonus,
  calculateStartScoredPoint,
  calculateFinishScoredPoint
} from './distanceCalculator.js';
import { getPrimaryClass } from './handicapData.js';

function firstDefinedValue(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return null;
}

function parseOptionalNumber(value) {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function parseOptionalBoolean(value) {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
    return true;
  }
  if (normalized === 'false' || normalized === '0' || normalized === 'no') {
    return false;
  }

  return null;
}

function calculateOutsideTurnBisector(bearingToPrev, bearingToNext) {
  const delta = normalizeAngle(bearingToNext - bearingToPrev);
  const insideBisector = (bearingToPrev + delta / 2 + 360) % 360;
  return (insideBisector + 180) % 360;
}

function normalizeTaskTypeLabel(value) {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const normalized = String(value).trim();
  if (!normalized) {
    return null;
  }

  const lower = normalized.toLowerCase();
  if (lower === 'aat') {
    return 'AAT';
  }
  if (lower === 'racing') {
    return 'Racing';
  }

  return normalized;
}

function normalizeTaskMetadata(taskData) {
  const taskType = normalizeTaskTypeLabel(
    firstDefinedValue(
      taskData.task_type,
      taskData.TaskType,
      taskData.task_additional_details?.TaskType
    )
  );

  const aatTimeHours = parseOptionalNumber(
    firstDefinedValue(
      taskData.aat_time_hours,
      taskData.AATTimeHours,
      taskData.task_additional_details?.AATTimeHours
    )
  );

  const startWindowMinutes = parseOptionalNumber(
    firstDefinedValue(
      taskData.start_window_minutes,
      taskData.StartWindowMinutes,
      taskData.task_additional_details?.StartWindowMinutes
    )
  );

  const regattaStart = parseOptionalBoolean(
    firstDefinedValue(
      taskData.regatta_start,
      taskData.RegattaStart,
      taskData.task_additional_details?.RegattaStart
    )
  );

  const normalizedTask = {
    ...taskData,
    task_type: taskType || (aatTimeHours !== null && aatTimeHours > 0 ? 'AAT' : 'Racing')
  };

  if (aatTimeHours !== null) {
    normalizedTask.aat_time_hours = aatTimeHours;
  } else if (normalizedTask.task_type === 'AAT') {
    normalizedTask.aat_time_hours = null;
  }

  if (startWindowMinutes !== null) {
    normalizedTask.start_window_minutes = startWindowMinutes;
  }

  if (regattaStart !== null) {
    normalizedTask.regatta_start = regattaStart;
  } else if (startWindowMinutes !== null) {
    normalizedTask.regatta_start = startWindowMinutes === 0;
  }

  return normalizedTask;
}

/**
 * Task Manager Class
 */
export class TaskManager {
  constructor() {
    this.tasks = new Map(); // taskId -> processed task data
  }

  /**
   * Process and store a task from the API
   * Pre-calculates bearings, bonuses, cumulative distances, and thresholds
   * @param {object} taskData - Raw task data from API
   * @returns {object} Processed task data
   */
  processTask(taskData) {
    if (!taskData || typeof taskData !== 'object') {
      console.warn('⚠️  Invalid task data provided to processTask');
      return null;
    }

    const normalizedTaskData = normalizeTaskMetadata(taskData);

    if (!normalizedTaskData.turnpoints || normalizedTaskData.turnpoints.length === 0) {
      console.warn(`⚠️  Task ${normalizedTaskData.id} has no turnpoints`);
      return null;
    }

    const turnpoints = normalizedTaskData.turnpoints;

    // Pre-calculate bearings used by sector geometry.
    for (let i = 0; i < turnpoints.length - 1; i++) {
      const tp = turnpoints[i];
      const nextTP = turnpoints[i + 1];
      tp.bearing = calculateBearing(tp.Lat, tp.Lon, nextTP.Lat, nextTP.Lon);
    }

    // Intermediate 90-degree FAI sectors face the outside of the turn. Using
    // only the outbound bearing points the slice down the next leg, which draws
    // and scores the wrong quadrant on out-and-return style tasks.
    for (let i = 1; i < turnpoints.length - 1; i++) {
      const tp = turnpoints[i];
      if (Math.abs(Number(tp.Angle) - 90) >= 1) {
        continue;
      }

      const prevTP = turnpoints[i - 1];
      const nextTP = turnpoints[i + 1];
      const bearingToPrev = calculateBearing(tp.Lat, tp.Lon, prevTP.Lat, prevTP.Lon);
      const bearingToNext = calculateBearing(tp.Lat, tp.Lon, nextTP.Lat, nextTP.Lon);
      tp.bearing = calculateOutsideTurnBisector(bearingToPrev, bearingToNext);
    }

    // For finish, use bearing from previous TP
    if (turnpoints.length > 1) {
      const finishTP = turnpoints[turnpoints.length - 1];
      const prevTP = turnpoints[turnpoints.length - 2];
      finishTP.bearing = calculateBearing(prevTP.Lat, prevTP.Lon, finishTP.Lat, finishTP.Lon);

      // Reverse bearing for finish line (add 180 degrees) if semicircle
      if (Math.abs(finishTP.Angle - 180) < 1) {
        finishTP.bearing = (finishTP.bearing + 180) % 360;
      }
    }

    const startScoredPoint = calculateStartScoredPoint(
      turnpoints[0],
      turnpoints.length > 1 ? turnpoints[1] : null
    );
    const finishScoredPoint = calculateFinishScoredPoint(
      turnpoints.length > 1 ? turnpoints[turnpoints.length - 2] : null,
      turnpoints[turnpoints.length - 1]
    );

    turnpoints[0].scoredLat = startScoredPoint.lat;
    turnpoints[0].scoredLon = startScoredPoint.lon;
    turnpoints[turnpoints.length - 1].scoredLat = finishScoredPoint.lat;
    turnpoints[turnpoints.length - 1].scoredLon = finishScoredPoint.lon;

    const scoringPoints = turnpoints.map((tp, index) => {
      if (index === 0) {
        return startScoredPoint;
      }

      if (index === turnpoints.length - 1) {
        return finishScoredPoint;
      }

      return { lat: tp.Lat, lon: tp.Lon };
    });

    // Calculate cumulative scored distances between turnpoints
    const cumulative = [0]; // Start is at 0
    for (let i = 1; i < turnpoints.length; i++) {
      const prevPoint = scoringPoints[i - 1];
      const currentPoint = scoringPoints[i];
      const legDistance = haversineDistance(
        prevPoint.lat,
        prevPoint.lon,
        currentPoint.lat,
        currentPoint.lon
      ) / 1000;

      cumulative.push(cumulative[i - 1] + legDistance);
    }

    // Calculate bonuses for each turnpoint
    const bonuses = [];
    for (let i = 0; i < turnpoints.length; i++) {
      const bonus = calculateTurnpointBonus(i, turnpoints);
      bonuses.push(bonus);
      turnpoints[i].bonus_km = bonus; // Store on TP for reference
    }

    // Calculate thresholds (distance at which TP is considered "touched")
    const thresholds = [];
    for (let i = 0; i < turnpoints.length; i++) {
      const radiusKm = turnpoints[i].RadiusMeters / 1000;
      const threshold = cumulative[i] - radiusKm;
      thresholds.push(Math.max(0, threshold)); // Don't go negative
    }

    // Calculate totals
    const startCylinderOffset_km = haversineDistance(
      turnpoints[0].Lat,
      turnpoints[0].Lon,
      startScoredPoint.lat,
      startScoredPoint.lon
    ) / 1000;
    const finishCylinderOffset_km = haversineDistance(
      turnpoints[turnpoints.length - 1].Lat,
      turnpoints[turnpoints.length - 1].Lon,
      finishScoredPoint.lat,
      finishScoredPoint.lon
    ) / 1000;
    const scoringTaskLength = cumulative[cumulative.length - 1];
    const totalBonus = bonuses.reduce((sum, b) => sum + b, 0);
    const netTaskLength = Math.max(0, scoringTaskLength - totalBonus);

    // Create processed task object
    const processedTask = {
      ...normalizedTaskData,
      turnpoints,
      cumulative,
      bonuses,
      thresholds,
      totalBonus,
      startCylinderOffset_km,
      finishCylinderOffset_km,
      startScoredPoint,
      finishScoredPoint,
      scoringTaskLength,
      netTaskLength
    };

    // Store in cache
    this.tasks.set(normalizedTaskData.id, processedTask);

    // Log detailed task information
    console.log(`   🎯 ${turnpoints.length} turnpoints`);

    for (let i = 0; i < turnpoints.length; i++) {
      const tp = turnpoints[i];
      const bonus = bonuses[i];
      const cumulativeDist = cumulative[i];
      const threshold = thresholds[i];
      const radiusKm = tp.RadiusMeters / 1000;

      let tpType = 'TP';
      if (i === 0) tpType = 'START';
      else if (i === turnpoints.length - 1) tpType = 'FINISH';

      console.log(`      ${i}. [${tpType}] ${tp.Name}`);
      console.log(`         Radius: ${radiusKm.toFixed(2)} km | Angle: ${tp.Angle}° | Bearing: ${tp.bearing ? tp.bearing.toFixed(1) + '°' : 'N/A'}`);
      console.log(`         Cumulative: ${cumulativeDist.toFixed(2)} km | Threshold: ${threshold.toFixed(2)} km | Bonus: ${bonus.toFixed(2)} km`);
    }

    console.log(`   🏁 Task Configuration:`);
    console.log(`      Task Type: ${processedTask.task_type}`);
    if (processedTask.task_type === 'AAT') {
      console.log(`      AAT Time: ${processedTask.aat_time_hours ? `${processedTask.aat_time_hours.toFixed(2)} hours` : 'N/A'}`);
    }
    console.log(`      Regatta Start: ${processedTask.regatta_start ? 'Yes' : 'No'}`);

    // Max start altitude is stored in the start turnpoint's MaxAltMeters field
    const startTP = turnpoints[0];
    const maxStartAlt = startTP?.MaxAltMeters ? parseFloat(startTP.MaxAltMeters) : null;

    // Condor uses 10000 or higher to indicate unlimited
    if (maxStartAlt && maxStartAlt < 10000) {
      console.log(`      Max Start Altitude: ${maxStartAlt.toFixed(0)} m`);
    } else {
      console.log(`      Max Start Altitude: Unlimited`);
    }

    // Determine glider class for the task
    const gliderClass = this.determineTaskClass(normalizedTaskData);
    processedTask.gliderClass = gliderClass;
    console.log(`      Glider Class: ${gliderClass}`);

    return processedTask;
  }

  /**
   * Determine the glider class for a task
   * 1. Check task_additional_details.Class if available
   * 2. Fallback: Analyze aircraft types from flights and determine class
   * 3. If multiple classes detected, return "All"
   *
   * @param {object} taskData - Raw task data from API
   * @returns {string} Glider class (e.g., "Standard", "15M", "18M", "Open", "Club", "All")
   */
  determineTaskClass(taskData) {
    // Method 1: Check task_additional_details.Class
    if (taskData.task_additional_details?.Class) {
      const taskClass = taskData.task_additional_details.Class;
// DEBUG:       console.log(`   ℹ️  Task class from task_additional_details: ${taskClass}`);
      return taskClass;
    }

    // Method 1b: Respect an explicit gliderClass already set on imported tasks
    if (typeof taskData.gliderClass === 'string' && taskData.gliderClass.trim()) {
      return taskData.gliderClass.trim();
    }

    // Method 2: Fallback - analyze aircraft types in flights
    // Note: This requires flight/aircraft data which may not be available during task processing
    // For now, return "All" as default when not specified
// DEBUG:     console.log(`   ℹ️  No task class specified in task_additional_details, defaulting to "All"`);
    return "All";
  }

  /**
   * Update task class based on aircraft types in flights
   * Call this after flight data is available
   *
   * @param {number} taskId - Task ID
   * @param {Array} aircraftTypes - Array of aircraft type names from flights
   * @returns {string} Determined glider class
   */
  updateTaskClassFromAircraft(taskId, aircraftTypes) {
    // Convert taskId to number if it's a string (fix for type mismatch)
    const numericTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;

    const task = this.tasks.get(numericTaskId);
    if (!task) {
      console.warn(`   ⚠️  Task ${taskId} not found in cache for class update`);
      return "All";
    }

    // If task already has an explicit class from import metadata, don't override
    if (task.task_additional_details?.Class) {
      return task.gliderClass;
    }
    if (typeof task.gliderClass === 'string' && task.gliderClass.trim() && task.gliderClass.trim().toLowerCase() !== 'all') {
      return task.gliderClass.trim();
    }

    // Analyze aircraft types to determine class
    const detectedClasses = new Set();

    for (const aircraftType of aircraftTypes) {
      const primaryClass = getPrimaryClass(aircraftType);
      if (primaryClass) {
        detectedClasses.add(primaryClass);
      }
    }

    // Determine final class
    let finalClass = "All";
    if (detectedClasses.size === 0) {
      finalClass = "All"; // No recognized aircraft
    } else if (detectedClasses.size === 1) {
      finalClass = Array.from(detectedClasses)[0]; // Single class
    } else {
      finalClass = "All"; // Multiple classes
    }

    // Update task with detected class
    task.gliderClass = finalClass;
// DEBUG:     console.log(`   ℹ️  Task ${taskId} class updated to: ${finalClass}`);

    return finalClass;
  }

  /**
   * Get a processed task by ID
   * @param {number} taskId - Task ID
   * @returns {object|null} Processed task data or null if not found
   */
  getTask(taskId) {
    return this.tasks.get(taskId) || null;
  }

  /**
   * Check if a task is loaded
   * @param {number} taskId - Task ID
   * @returns {boolean}
   */
  hasTask(taskId) {
    return this.tasks.has(taskId);
  }

  /**
   * Clear a specific task from cache
   * @param {number} taskId - Task ID
   */
  clearTask(taskId) {
    if (this.tasks.delete(taskId)) {
// DEBUG:       console.log(`🗑️  Task ${taskId} cleared from cache`);
    }
  }

  /**
   * Clear all tasks from cache
   */
  clearAllTasks() {
    this.tasks.clear();
// DEBUG:     console.log('🗑️  All tasks cleared from cache');
  }
}
